/**
 * 数据集相关API服务
 * 包含数据集的增删查改、状态切换等功能
 */

import { fetcher } from '../base/fetcher'
import type {
  DatasetListResponse,
  Dataset,
  GetDatasetListRequest,
} from '../types/dataset.type'

/**
 * 数据集相关API路径常量
 */
export const DATASET_API_PATHS = {
  /** 获取数据集列表 */
  LIST: '/knowledgeBase/dataset/list',
  /** 删除数据集 */
  DELETE: '/knowledgeBase/dataset/delById',
  /** 新增/编辑数据集 */
  SAVE_OR_UPDATE: '/knowledgeBase/dataset/saveOrUpdate',
  /** 切换数据集状态 */
  TOGGLE_STATE: '/knowledgeBase/dataset/toggleState',
} as const

/**
 * 数据集服务对象
 * 提供所有数据集相关的API调用方法
 */
export const datasetService = {
  /**
   * 获取数据集列表
   * @param params 查询参数（知识库ID、搜索关键词、状态等）
   * @param signal 可选的 AbortSignal 用于取消请求
   * @returns 数据集列表响应
   */
  getDatasetList: (
    params: GetDatasetListRequest,
    signal?: AbortSignal
  ): Promise<DatasetListResponse> => {
    // 将参数转换为字符串类型以符合fetcher要求
    const stringParams: Record<string, string> = {}
    if (params.knowledgeBaseId)
      stringParams.knowledgeBaseId = params.knowledgeBaseId
    if (params.search) stringParams.search = params.search
    if (params.status !== undefined)
      stringParams.status = params.status.toString()

    return fetcher.get<DatasetListResponse>(DATASET_API_PATHS.LIST, {
      params: stringParams,
      signal,
    })
  },

  /**
   * 删除数据集
   * @param id 数据集ID
   * @returns 删除操作结果
   */
  deleteDataset: (id: string): Promise<boolean> =>
    fetcher.post<boolean>(DATASET_API_PATHS.DELETE, { id }),

  /**
   * 新增或编辑数据集
   * @param data 数据集信息（新增时不传id，编辑时必传id）
   * @returns 操作结果，返回包含id的对象
   */
  updateDataset: (data: Partial<Dataset>): Promise<{ id: string }> =>
    fetcher.post<{ id: string }>(DATASET_API_PATHS.SAVE_OR_UPDATE, data),

  /**
   * 切换数据集状态
   * @param id 数据集ID
   * @param status 数据集状态
   * @returns 切换操作结果
   */
  toggleDatasetState: (id: string, status: number): Promise<boolean> =>
    fetcher.post<boolean>(DATASET_API_PATHS.TOGGLE_STATE, { id, status }),
}
